import { useState } from 'react';
import { Truck } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryAccountsService from '../../../services/nurseryAccounts';
import { errorMessage } from '../../../utils/apiResponse';
import { money } from '../../../utils/nurseryMoney';
import type { InvoiceFreight } from '../../../types/nurseryAccounts';

/** Where the freight figure came from, in a sentence. */
function basisText(basis: InvoiceFreight['basis']): string {
  switch (basis) {
    case 'zone': return "worked out from the order's delivery zone";
    case 'customer': return 'the freight rate set for this customer';
    case 'none': return 'this customer is set to no freight';
    case 'website': return 'quoted at checkout on the website';
    case 'carrier': return 'an outside carrier, typed at invoicing';
    default: return 'typed';
  }
}

/**
 * The freight on a draft invoice. An order going by an outside carrier has
 * none until someone invoicing types the carrier's price; any other freight
 * was worked out from the customer's rule or the delivery zone, and an
 * Accounts Admin can override it. The server keeps the figure on the order
 * and writes the draft again; once authorised, a credit note changes it.
 */
export function InvoiceFreightCard({ invoiceId, freight, isDraft, isAccounts, isAdmin, onSaved, onChoose }: {
  invoiceId: number;
  freight: InvoiceFreight;
  isDraft: boolean;
  isAccounts: boolean;
  isAdmin: boolean;
  onSaved: () => Promise<void> | void;
  /** Opens the freight question for a customer with none set. */
  onChoose?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canEdit = isDraft && !freight.paid_online && (freight.carrier ? isAccounts : isAdmin);
  const showForm = canEdit && (editing || freight.missing);

  const save = async (amount: number | null) => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.setInvoiceFreight(invoiceId, amount);
      if (!res.success) {
        setError(errorMessage(res, 'Could not save the freight'));
      } else {
        setEditing(false);
        setValue('');
        await onSaved();
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not save the freight'));
    }
    setBusy(false);
  };

  const typed = value.trim() === '' ? NaN : Number(value);
  const valid = Number.isFinite(typed) && typed >= 0;

  return (
    <div className={`border rounded-xl p-4 space-y-2 ${freight.missing ? 'bg-amber-50 border-amber-200' : 'bg-white border-neutral-200'}`}>
      <div className="flex flex-wrap items-start gap-2">
        <Truck className={`h-5 w-5 shrink-0 ${freight.missing ? 'text-amber-600' : 'text-neutral-400'}`} />
        <div className="flex-1 min-w-0 text-sm break-words">
          {freight.missing ? (
            <>
              <p className="font-semibold text-amber-900">This order goes by an outside carrier and has no freight yet.</p>
              <p className="text-amber-900">
                Enter the carrier's price, ex GST. The invoice cannot be authorised until it has one, and at
                despatch it stays a draft until it does.
              </p>
            </>
          ) : (
            <>
              <p className="text-neutral-800">
                <span className="font-semibold">{freight.label}:</span> {money(freight.amount)} ex GST
                <span className="text-neutral-500"> · {basisText(freight.basis)}</span>
              </p>
              {freight.typed != null && (
                <p className="text-xs text-neutral-500">
                  Typed{freight.typed_by ? ` by ${freight.typed_by}` : ''}.
                  {!freight.carrier && freight.worked_out != null && ` Worked out it would be ${money(freight.worked_out)}.`}
                </p>
              )}
              {freight.unset && isDraft && (
                <p className="text-xs text-amber-800">
                  This customer has no freight set, so it fell back to the delivery zone. Set what it pays.
                </p>
              )}
              {freight.paid_online && isDraft && (
                <p className="text-xs text-neutral-500">Paid online with its delivery, so the freight cannot change.</p>
              )}
            </>
          )}
        </div>
        {!showForm && (canEdit || (freight.unset && isDraft && onChoose)) && (
          <div className="flex flex-wrap gap-2">
            {freight.unset && isDraft && onChoose && (
              <Button size="sm" onClick={onChoose}>Set freight</Button>
            )}
            {canEdit && (
              <Button variant="outline" size="sm" onClick={() => { setEditing(true); setValue(String(freight.amount)); }}>
                {freight.carrier ? 'Change freight' : 'Override freight'}
              </Button>
            )}
            {canEdit && freight.typed != null && !freight.carrier && (
              <Button variant="outline" size="sm" disabled={busy} onClick={() => save(null)}>
                Use worked-out freight
              </Button>
            )}
          </div>
        )}
      </div>

      {showForm && (
        <div className="flex flex-wrap items-end gap-2 pl-0 sm:pl-7">
          <label className="block min-w-0">
            <span className="block text-xs font-medium text-neutral-600 mb-1">
              {freight.carrier ? "Carrier's price, ex GST" : 'Freight, ex GST'}
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-40 max-w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label={freight.carrier ? "Carrier's price, ex GST" : 'Freight, ex GST'}
            />
          </label>
          <Button size="sm" disabled={busy || !valid} onClick={() => save(typed)}>
            {busy ? 'Saving…' : 'Save freight'}
          </Button>
          {!freight.missing && (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => { setEditing(false); setError(null); }}>
              Cancel
            </Button>
          )}
        </div>
      )}
      {!canEdit && freight.missing && isDraft && (
        <p className="text-xs text-amber-900">Someone with an Accounts seat enters it.</p>
      )}
      {error && <p className="text-sm text-red-700 break-words">{error}</p>}
    </div>
  );
}
